import Head from "next/head";
import { FormEvent, useState } from "react";
import { ZodError, z } from "zod";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useRouter } from "next/router";
import { AuthUserSchema } from "@/lib/schemas/authValidationSchema";
import Layout from "@/components/Layout";
import {
  Alert,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  VStack,
} from "@chakra-ui/react";
import InputErrorMessage from "@/components/InputErrorMessage";
import { formatError } from "@/lib/utils";
import { AuthApiError } from "@supabase/supabase-js";
import ChakraLink from "@/components/ChakraLink";

type FormData = z.infer<typeof AuthUserSchema>;

export default function SignIn() {
  const supabase = useSupabaseClient();
  const router = useRouter();
  const [errors, setErrors] = useState<FormData>();
  const [message, setMessage] = useState<string>("");
  const [formSuccess, setFormSuccess] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // reset all states
    setFormSuccess(false);
    setErrors(undefined);
    setMessage("");

    const email = formData.email;
    const password = formData.password;

    try {
      AuthUserSchema.parse({ email, password });
    } catch (err) {
      if (err instanceof ZodError) {
        const errs = formatError(err) as FormData;
        setErrors(errs);
        return;
      }
    }

    supabase.auth
      .signInWithPassword({
        email,
        password,
      })
      .then(({ data, error }) => {
        if (!error && data.user) {
          // reset form
          setFormData({ email: "", password: "" });
          router.push("/");
        } else {
          setMessage("Invalid credentials.");
          return;
        }
      })
      .catch(error => {
        if (error) {
          if (error instanceof AuthApiError && error.status === 400) {
            setMessage("Invalid credentials.");
            return;
          }
          setMessage(error.message);
          return;
        }
      });
  };

  return (
    <>
      <Head>
        <title>Sign in</title>
      </Head>
      <Layout>
        <Center gridArea="content" h="full">
          <VStack
            bg="white"
            rounded="md"
            textColor="black"
            p={8}
            w="md"
            spacing={4}>
            <Heading size="md">Sign in</Heading>
            <VStack
              as="form"
              w="full"
              spacing={8}
              fontSize="md"
              onSubmit={e =>
                handleSubmit(e as unknown as FormEvent<HTMLFormElement>)
              }>
              <FormControl>
                <FormLabel htmlFor="email">Email</FormLabel>
                <Input
                  id="email"
                  name="email"
                  type="text"
                  value={formData?.email ?? ""}
                  onChange={ev =>
                    setFormData({ ...formData, email: ev.target.value })
                  }
                />
              </FormControl>

              {errors?.email ? (
                <InputErrorMessage>{errors?.email}</InputErrorMessage>
              ) : null}
              <FormControl>
                <FormLabel htmlFor="password">Password</FormLabel>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData?.password ?? ""}
                  onChange={ev =>
                    setFormData({ ...formData, password: ev.target.value })
                  }
                />
              </FormControl>
              {errors?.password ? (
                <InputErrorMessage>{errors?.password}</InputErrorMessage>
              ) : null}

              {message ? (
                <Alert status={formSuccess ? "success" : "error"}>
                  {message}
                </Alert>
              ) : null}

              <FormControl>
                <Button w="full" type="submit">
                  Sign in
                </Button>
              </FormControl>
              <ChakraLink href="/auth/forgot-password">
                Forgot Password?
              </ChakraLink>
            </VStack>
          </VStack>
        </Center>
      </Layout>
    </>
  );
}
